import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AdminDashboard from "./pages/dashboard/AdminDashboard";
import TeacherDashboard from "./pages/dashboard/TeacherDashboard";
import StudentDashboard from "./pages/dashboard/StudentDashboard";
import PartnerDashboard from "./pages/dashboard/PartnerDashboard";
import StudentsPage from "./pages/StudentsPage";
import FilesPage from "./pages/FilesPage";
import PartnersPage from "./pages/PartnersPage";
import PaymentsPage from "./pages/PaymentsPage";
import MessagesPage from "./pages/MessagesPage";
import StatisticsPage from "./pages/StatisticsPage";
import SettingsPage from "./pages/SettingsPage";
import AppointmentsPage from "./pages/AppointmentsPage";
import ResourcesPage from "./pages/ResourcesPage";
import ResourcesAdminPage from "./pages/ResourcesAdminPage";
import MyCandidatesPage from "./pages/MyCandidatesPage";
import StudentDetailPage from "./pages/StudentDetailPage";
import ContactMessagesPage from "./pages/ContactMessagesPage";
import LeadsPage from "./pages/LeadsPage";

// Courses pages
import CourseList from "./pages/courses/CourseList";
import CourseDetail from "./pages/courses/CourseDetail";
import SessionView from "./pages/courses/SessionView";
import StudentCourses from "./pages/student/StudentCourses";
import StudentCourseDetail from "./pages/student/StudentCourseDetail";

// Dossiers pages
import DossierList from "./pages/dossiers/DossierList";
import DossierDetail from "./pages/dossiers/DossierDetail";
import StudentDossier from "./pages/student/StudentDossier";

// E-Learning pages
import CourseLearningPage from "./pages/elearning/CourseLearningPage";
import QuizPage from "./pages/elearning/QuizPage";
import CourseContentManager from "./pages/elearning/CourseContentManager";

// Booking pages
import PublicBookingPage from "./pages/booking/PublicBookingPage";
import BookingsCalendarPage from "./pages/booking/BookingsCalendarPage";
import AppointmentTypesPage from "./pages/booking/AppointmentTypesPage";

// Arrivals, Analytics, Admin pages
import ArrivalTrackingPage from "./pages/arrivals/ArrivalTrackingPage";
import TeacherPerformancePage from "./pages/analytics/TeacherPerformancePage";
import BackupManagementPage from "./pages/admin/BackupManagementPage";
import CreateUserPage from "./pages/CreateUserPage";
import UsersPage from "./pages/UsersPage";

// Layout
import DashboardLayout from "./components/layout/DashboardLayout";

// Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

// Protected route component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    const dashboardPath = {
      admin: '/dashboard/admin',
      resp_peda: '/dashboard/admin',
      resp_admin: '/dashboard/admin',
      teacher: '/dashboard/teacher',
      student: '/dashboard/student',
      partner: '/dashboard/partner',
    };
    return <Navigate to={dashboardPath[user?.role] || '/login'} replace />;
  }

  return children;
};

// Auth redirect component
const AuthRedirect = () => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return null;
  }

  if (isAuthenticated && user) {
    const dashboardPath = {
      admin: '/dashboard/admin',
      resp_peda: '/dashboard/admin',
      resp_admin: '/dashboard/admin',
      teacher: '/dashboard/teacher',
      student: '/dashboard/student',
      partner: '/dashboard/partner',
    };
    return <Navigate to={dashboardPath[user.role] || '/dashboard/admin'} replace />;
  }

  return <Navigate to="/login" replace />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Landing Page - Public */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      
      {/* Public Booking Page */}
      <Route path="/rendez-vous" element={<PublicBookingPage />} />

      {/* E-Learning Student (fullscreen, no dashboard sidebar) */}
      <Route
        path="/learn/:courseId"
        element={
          <ProtectedRoute allowedRoles={['student', 'admin', 'teacher']}>
            <CourseLearningPage />
          </ProtectedRoute>
        }
      />

      {/* Protected routes with layout */}
      <Route element={<DashboardLayout />}>
        {/* Admin Dashboard */}
        <Route
          path="/dashboard/admin"
          element={
            <ProtectedRoute allowedRoles={['admin', 'resp_peda', 'resp_admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        {/* Teacher Dashboard */}
        <Route
          path="/dashboard/teacher"
          element={
            <ProtectedRoute allowedRoles={['teacher']}>
              <TeacherDashboard />
            </ProtectedRoute>
          }
        />

        {/* Student Dashboard */}
        <Route
          path="/dashboard/student"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <StudentDashboard />
            </ProtectedRoute>
          }
        />

        {/* Partner Dashboard */}
        <Route
          path="/dashboard/partner"
          element={
            <ProtectedRoute allowedRoles={['partner']}>
              <PartnerDashboard />
            </ProtectedRoute>
          }
        />

        {/* My Candidates - Partner only */}
        <Route
          path="/my-candidates"
          element={
            <ProtectedRoute allowedRoles={['partner']}>
              <MyCandidatesPage />
            </ProtectedRoute>
          }
        />

        {/* Students Management - Admin, Resp Peda, Resp Admin, Teacher */}
        <Route
          path="/students"
          element={
            <ProtectedRoute allowedRoles={['admin', 'teacher', 'resp_peda', 'resp_admin']}>
              <StudentsPage />
            </ProtectedRoute>
          }
        />

        {/* Bookings Calendar - Admin */}
        <Route
          path="/bookings"
          element={
            <ProtectedRoute allowedRoles={['admin', 'resp_admin']}>
              <BookingsCalendarPage />
            </ProtectedRoute>
          }
        />
        
        {/* Appointment Types Management - Admin */}
        <Route
          path="/booking-types"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AppointmentTypesPage />
            </ProtectedRoute>
          }
        />

        {/* Student Detail */}
        <Route
          path="/students/:id"
          element={
            <ProtectedRoute allowedRoles={['admin', 'teacher', 'resp_peda', 'resp_admin']}>
              <StudentDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Courses Management - Admin, Teacher, Resp Peda */}
        <Route
          path="/courses"
          element={
            <ProtectedRoute allowedRoles={['admin', 'teacher', 'resp_peda']}>
              <CourseList />
            </ProtectedRoute>
          }
        />

        {/* Course Detail */}
        <Route
          path="/courses/:id"
          element={
            <ProtectedRoute allowedRoles={['admin', 'teacher', 'resp_peda', 'student']}>
              <CourseDetail />
            </ProtectedRoute>
          }
        />

        {/* Session View */}
        <Route
          path="/courses/:id/session/:sessionId"
          element={
            <ProtectedRoute allowedRoles={['admin', 'teacher', 'resp_peda']}>
              <SessionView />
            </ProtectedRoute>
          }
        />

        {/* Student's courses view */}
        <Route
          path="/student/courses"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <StudentCourses />
            </ProtectedRoute>
          }
        />

        {/* Student's course detail */}
        <Route
          path="/student/courses/:id"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <StudentCourseDetail />
            </ProtectedRoute>
          }
        />

        {/* My Courses - legacy route redirect */}
        <Route
          path="/my-courses"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <StudentCourses />
            </ProtectedRoute>
          }
        />

        {/* Files Management - Admin and Resp Admin */}
        <Route
          path="/files"
          element={
            <ProtectedRoute allowedRoles={['admin', 'resp_admin']}>
              <FilesPage />
            </ProtectedRoute>
          }
        />

        {/* Dossiers Management - Admin and Resp Admin */}
        <Route
          path="/dossiers"
          element={
            <ProtectedRoute allowedRoles={['admin', 'resp_admin']}>
              <DossierList />
            </ProtectedRoute>
          }
        />

        {/* Dossier Detail */}
        <Route
          path="/dossiers/:studentId"
          element={
            <ProtectedRoute allowedRoles={['admin', 'resp_admin']}>
              <DossierDetail />
            </ProtectedRoute>
          }
        />

        {/* Student's dossier */}
        <Route
          path="/student/dossier"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <StudentDossier />
            </ProtectedRoute>
          }
        />

        {/* Student's files - legacy redirect to dossier */}
        <Route
          path="/my-files"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <StudentDossier />
            </ProtectedRoute>
          }
        />

        {/* E-Learning Routes */}
        <Route
          path="/quiz/:quizId"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <QuizPage />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/admin/courses/:courseId/content"
          element={
            <ProtectedRoute allowedRoles={['admin', 'teacher', 'resp_peda']}>
              <DashboardLayout>
                <CourseContentManager />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Payments - Admin and Resp Admin */}
        <Route
          path="/payments"
          element={
            <ProtectedRoute allowedRoles={['admin', 'resp_admin']}>
              <PaymentsPage />
            </ProtectedRoute>
          }
        />

        {/* Student's payments */}
        <Route
          path="/my-payments"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <PaymentsPage />
            </ProtectedRoute>
          }
        />

        {/* Partners Management - Admin and Resp Admin */}
        <Route
          path="/partners"
          element={
            <ProtectedRoute allowedRoles={['admin', 'resp_admin']}>
              <PartnersPage />
            </ProtectedRoute>
          }
        />

        {/* Messages - All authenticated users */}
        <Route
          path="/messages"
          element={
            <ProtectedRoute allowedRoles={['admin', 'resp_admin', 'resp_peda', 'teacher', 'student', 'partner']}>
              <MessagesPage />
            </ProtectedRoute>
          }
        />

        {/* Statistics - Admin only */}
        <Route
          path="/statistics"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <StatisticsPage />
            </ProtectedRoute>
          }
        />

        {/* Settings - All authenticated users */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute allowedRoles={['admin', 'resp_admin', 'resp_peda', 'teacher', 'student', 'partner']}>
              <SettingsPage />
            </ProtectedRoute>
          }
        />

        {/* Appointments - Students */}
        <Route
          path="/appointments"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <AppointmentsPage />
            </ProtectedRoute>
          }
        />

        {/* Resources - Students */}
        <Route
          path="/resources"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <ResourcesPage />
            </ProtectedRoute>
          }
        />

        {/* Resources Admin - Admin only */}
        <Route
          path="/resources-admin"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ResourcesAdminPage />
            </ProtectedRoute>
          }
        />

        {/* Contact Messages - Admin only */}
        <Route
          path="/contact-messages"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ContactMessagesPage />
            </ProtectedRoute>
          }
        />

        {/* Leads Management - Admin only */}
        <Route
          path="/leads"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <LeadsPage />
            </ProtectedRoute>
          }
        />

        {/* Arrival Tracking - Admin and Partner */}
        <Route
          path="/arrivals"
          element={
            <ProtectedRoute allowedRoles={['admin', 'resp_admin', 'partner']}>
              <ArrivalTrackingPage />
            </ProtectedRoute>
          }
        />

        {/* Teacher Performance Analytics - Admin */}
        <Route
          path="/analytics/teachers"
          element={
            <ProtectedRoute allowedRoles={['admin', 'resp_peda']}>
              <TeacherPerformancePage />
            </ProtectedRoute>
          }
        />

        {/* Backup Management - Admin only */}
        <Route
          path="/admin/backups"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <BackupManagementPage />
            </ProtectedRoute>
          }
        />

        {/* Create User - Admin only */}
        <Route
          path="/admin/create-user"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <CreateUserPage />
            </ProtectedRoute>
          }
        />

        {/* Users Management - Admin only */}
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <UsersPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Catch all - redirect to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

import { LanguageProvider } from "./contexts/LanguageContext";

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <LanguageProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </LanguageProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
